import type { CSS } from '@stitches/react';
import { memo, useMemo } from 'react';

import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { CarModel } from '@/components/sections/product-compare/car-selection-card';
import PriceDialog from '@/components/sections/product-compare/price-dialog';
import PriceHoverCard from '@/components/sections/product-compare/price-hover-card';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { formatPrice } from '@/components/shared/utility/format';
import { config } from '@/stitches.config';

export interface TooltipProps {
  show: boolean;
  data: any[];
}
interface CarSelectionPriceProps {
  css?: CSS;
  index: number;
  model: CarModel;
  prefix?: boolean;
  tooltip?: TooltipProps;
  remark?: string;
}

// TO-DO shared component tooltip
const Tooltip = memo(
  ({ models, dataTest }: { models: any[]; dataTest?: string }) => {
    const isMobile = useMediaQuery(config.media.maxlg);
    return isMobile ? (
      <PriceDialog models={models} test={{ dataTest }} />
    ) : (
      <PriceHoverCard models={models} test={{ dataTest }} />
    );
  }
);

export const CarSelectionPrice: React.FC<CarSelectionPriceProps> = memo(
  ({ css, index, model, prefix, remark, tooltip }: CarSelectionPriceProps) => {
    const priceModel = useMemo(() => {
      const carPrice =
        Number(model?.price) + Number(model?.modelColor?.price ?? 0);
      return formatPrice(carPrice);
    }, [model?.modelColor?.price, model?.price]);

    return (
      <>
        <Flex
          data-test={`panel_modelStartedPrice_${index}`}
          direction="column"
          columnGap="2"
          justifyContent="center"
          alignItems="center"
          css={css}
        >
          <Flex
            direction={{ '@initial': 'column', '@lg': 'row' }}
            columnGap="2"
            justifyContent="center"
            alignItems={{ '@initial': 'center', '@lg': 'baseline' }}
          >
            {!!prefix && (
              <Text variant="text-gray-darkest" size="text-body-2">
                เริ่มต้น
              </Text>
            )}
            <Flex
              direction="row"
              columnGap="2"
              justifyContent="center"
              alignItems="center"
            >
              <Text
                data-test={`model_price_${index}`}
                css={{ textAlign: 'center' }}
                size="text-body-1"
                font="bold"
                variant="text-gray-darkest"
              >
                {priceModel}
              </Text>
              {!!tooltip?.show && (
                <Tooltip
                  models={tooltip?.data ?? [model]}
                  dataTest={`price_tooltip_${index}`}
                />
              )}
            </Flex>
          </Flex>
          {!!remark && (
            <Text
              css={{ textAlign: 'center' }}
              size="text-caption"
              variant="text-gray-darkest"
              data-test={`panel_modelRemark_${index}`}
            >
              {remark}
            </Text>
          )}
        </Flex>
      </>
    );
  }
);

Tooltip.displayName = 'Tooltip';
CarSelectionPrice.displayName = 'CarSelectionPrice';
